import React, { useEffect, useState } from 'react';
import { Plus, Pencil, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useStore } from '@/context/StoreContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ALL_PERMISSIONS, PERMISSION_LABELS, ROLE_DEFAULTS, ROLE_LABELS } from '@/lib/permissions';

const EMPTY = { name: '', email: '', password: '', role: 'staff', permissions: ROLE_DEFAULTS.staff };
const ROLE_DESC = {
  owner: 'Full access to everything, including admin user management.',
  manager: 'Store operations: catalog, orders, customers, marketing and analytics. No Settings or Home/CMS.',
  staff: 'Limited access: orders, inventory and related customer/order details only.',
};

export default function AdminUsers() {
  const { user: me } = useStore();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/admin/users').then((r) => setUsers(r.data)).catch(() => toast.error('Could not load admin users'));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role === 'admin' ? 'owner' : u.role, permissions: u.permissions.filter((p) => p !== 'admin_users') });
    setOpen(true);
  };

  const setRole = (role) => setForm((f) => ({ ...f, role, permissions: role === 'owner' ? [] : (ROLE_DEFAULTS[role] || []) }));
  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    if (!editing && form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), role: form.role, permissions: form.role === 'owner' ? null : form.permissions };
      if (form.password) payload.password = form.password;
      if (editing) await api.put(`/admin/users/${editing.id}`, payload);
      else await api.post('/admin/users', { ...payload, email: form.email.trim(), password: form.password });
      toast.success(editing ? 'Admin user updated' : 'Admin user created');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? `${u.name} deactivated` : `${u.name} activated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const permissiveList = ALL_PERMISSIONS.filter((p) => p !== 'admin_users');

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Admin Users</h1>
        <button onClick={openCreate} data-testid="admin-user-add-btn" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-sm font-semibold text-white"><Plus size={15} /> Add Admin User</button>
      </div>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-[hsl(var(--gaze-ivory))] text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Access</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid="admin-user-row">
                <td className="p-3 font-medium">{u.name}{u.id === me?.id && <span className="ml-2 rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 text-[10px] text-muted-foreground">You</span>}</td>
                <td className="p-3 text-muted-foreground">{u.email}</td>
                <td className="p-3"><span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--gaze-ivory))] px-3 py-1 text-xs font-medium"><ShieldCheck size={12} className="text-[hsl(var(--gaze-gold))]" /> {ROLE_LABELS[u.role] || u.role}</span></td>
                <td className="p-3 text-xs text-muted-foreground">{(u.role === 'admin' || u.role === 'owner') ? 'Full access' : u.permissions.map((p) => PERMISSION_LABELS[p]).join(', ') || 'No access'}</td>
                <td className="p-3">{u.active ? <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">Active</span> : <span className="rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-600">Deactivated</span>}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(u)} data-testid="admin-user-edit-btn" className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-semibold"><Pencil size={12} /> Edit</button>
                    {u.id !== me?.id && (
                      <button onClick={() => toggleActive(u)} data-testid="admin-user-toggle-active-btn" className={`rounded-full px-3 py-1 text-xs font-semibold ${u.active ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>{u.active ? 'Deactivate' : 'Activate'}</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Admin User' : 'Add Admin User'}</DialogTitle>
            <DialogDescription>{editing ? 'Update role, permissions or reset password.' : 'Create a new admin account with a role and module permissions.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Name</label>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="admin-user-name-input" className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Email</label>
                <input value={form.email} disabled={!!editing} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="admin-user-email-input" className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm disabled:bg-[hsl(var(--gaze-ivory))] disabled:text-muted-foreground" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">{editing ? 'New Password (leave blank to keep current)' : 'Password'}</label>
              <input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="admin-user-password-input" className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Role</label>
              <select value={form.role} disabled={editing?.id === me?.id} onChange={(e) => setRole(e.target.value)} data-testid="admin-user-role-select" className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm disabled:bg-[hsl(var(--gaze-ivory))]">
                <option value="owner">Owner / Super Admin</option>
                <option value="manager">Manager</option>
                <option value="staff">Staff</option>
              </select>
              <p className="mt-1 text-xs text-muted-foreground">{ROLE_DESC[form.role]}</p>
            </div>
            {form.role !== 'owner' && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Module Permissions</label>
                <div className="mt-2 grid grid-cols-2 gap-2" data-testid="admin-user-permissions">
                  {permissiveList.map((p) => (
                    <label key={p} className="flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                      <input type="checkbox" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} data-testid={`perm-checkbox-${p}`} className="accent-[hsl(var(--primary))]" />
                      {PERMISSION_LABELS[p]}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <button onClick={save} disabled={busy} data-testid="admin-user-save-btn" className="w-full rounded-full bg-[hsl(var(--primary))] py-2.5 text-sm font-semibold text-white disabled:opacity-60">{busy ? 'Saving...' : editing ? 'Save Changes' : 'Create Admin User'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
